import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import SearchSvg from '../other/svg/SearchSvg'
import TrianglesMiniSvg from '../other/svg/TrianglesMiniSvg'
import XMarkSvg from '../other/svg/XMarkSvg'
import { fieldBoxShadow } from './FormTextInput'

export interface SearchSelectItem {
  id: string
  title: string
  subtitle: string
}

interface SearchSelectProps {
  selectedId: string
  selectedLabel: string
  placeholder: string
  searchPlaceholder: string
  loadingLabel: string
  emptyLabel: string
  error?: boolean
  items: SearchSelectItem[]
  loading: boolean
  onOpen: () => void
  onClose: () => void
  onSearchChange: (value: string) => void
  onSelect: (item: SearchSelectItem) => void
  onClear: () => void
}

interface MenuPosition {
  top: number
  left: number
  width: number
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const SelectedCheckIcon = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    className="absolute left-3 size-3.5 text-foregrounds-fg-base">
    <path d="M3 7.2L5.7 10L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const SearchSelect = ({
  selectedId,
  selectedLabel,
  placeholder,
  searchPlaceholder,
  loadingLabel,
  emptyLabel,
  error = false,
  items,
  loading,
  onOpen,
  onClose,
  onSearchChange,
  onSelect,
  onClear
}: SearchSelectProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const controlRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const hasSelection = selectedId.length > 0 && selectedLabel.length > 0
  const displayLabel = hasSelection ? selectedLabel : placeholder

  useEffect(() => {
    if (isOpen) {
      setSearchValue('')
      onOpen()
    }

    return () => {
      if (isOpen) {
        onClose()
      }
    }
  }, [isOpen, onClose, onOpen])

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      if (controlRef.current === null) {
        return
      }
      const rect = controlRef.current.getBoundingClientRect()
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      })
    }

    if (isOpen) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
    } else {
      setMenuPosition(null)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus()
    }
  }, [isOpen, menuPosition])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const toggleOpen = (): void => {
    setIsOpen((open) => !open)
  }

  const handleSearchChange = (value: string): void => {
    setSearchValue(value)
    onSearchChange(value)
  }

  const handleClear = (event: React.MouseEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    onClear()
    setIsOpen(true)
  }

  const handleSelect = (item: SearchSelectItem): void => {
    onSelect(item)
    setIsOpen(false)
  }

  const renderMenuBody = (): JSX.Element => {
    if (loading && items.length === 0) {
      return (
        <div className="self-stretch py-8 text-center text-foregrounds-fg-muted text-sm font-medium font-Montserrat">
          {loadingLabel}
        </div>
      )
    }
    if (items.length === 0) {
      return (
        <div className="self-stretch py-8 text-center text-foregrounds-fg-muted text-sm font-medium font-Montserrat">
          {emptyLabel}
        </div>
      )
    }
    return (
      <>
        {items.map((item) => {
          const isSelected = item.id === selectedId
          return (
            <button
              type="button"
              key={item.id}
              onClick={() => {
                handleSelect(item)
              }}
              className="self-stretch relative pl-9 pr-3 py-2 rounded-sm inline-flex justify-start items-center gap-2 bg-backgrounds-bg-field-component hover:bg-backgrounds-bg-component-hover">
              {isSelected && <SelectedCheckIcon />}
              <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5 min-w-0">
                <div className="self-stretch text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5 truncate">
                  {item.title}
                </div>
                {item.subtitle.length > 0 && (
                  <div className="self-stretch text-left text-foregrounds-fg-muted text-xs font-medium font-Montserrat leading-4 truncate">
                    {item.subtitle}
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </>
    )
  }

  return (
    <div className="self-stretch relative">
      <div
        ref={controlRef}
        className={`self-stretch w-full h-11 pl-4 pr-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center overflow-hidden ${
          isOpen ? 'relative z-[150]' : ''
        }`}
        style={{ boxShadow: fieldBoxShadow(error) }}>
        <button
          type="button"
          onClick={toggleOpen}
          className={`flex-1 h-full text-left text-sm font-medium font-Montserrat leading-5 truncate bg-transparent ${
            hasSelection ? 'text-foregrounds-fg-base font-semibold' : 'text-foregrounds-fg-muted'
          }`}>
          {displayLabel}
        </button>
        {hasSelection && (
          <button
            type="button"
            onClick={handleClear}
            className="size-8 flex justify-center items-center shrink-0"
            aria-label="Clear">
            <XMarkSvg className="size-[15px] text-buttons-text-secondary" />
          </button>
        )}
        <button type="button" onClick={toggleOpen} className="size-8 flex justify-center items-center shrink-0">
          <TrianglesMiniSvg className="size-[15px] text-foregrounds-fg-muted" />
        </button>
      </div>
      {isOpen &&
        menuPosition !== null &&
        createPortal(
          <>
            <div
              role="presentation"
              className="fixed inset-0 z-[100]"
              onClick={() => {
                setIsOpen(false)
              }}
            />
            <div
              className="fixed z-[200] p-1 bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-start overflow-hidden"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                boxShadow: menuShadow
              }}>
              <div className="self-stretch px-2 py-1.5 mb-1 rounded-md bg-backgrounds-bg-field-component inline-flex justify-start items-center gap-2">
                <SearchSvg className="size-5 text-foregrounds-fg-muted shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  autoComplete="off"
                  onChange={(event) => {
                    handleSearchChange(event.target.value)
                  }}
                  placeholder={searchPlaceholder}
                  className="flex-1 h-8 bg-transparent outline-none text-sm font-medium font-Montserrat leading-5 text-foregrounds-fg-base placeholder:text-foregrounds-fg-muted"
                />
              </div>
              <div className="self-stretch max-h-72 overflow-y-auto flex flex-col justify-start items-start">
                {renderMenuBody()}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default SearchSelect
